import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Text } from '@actual-app/components/text';
import { theme } from '@actual-app/components/theme';
import { Toggle } from '@actual-app/components/toggle';
import { View } from '@actual-app/components/view';

import { useAuth } from '#auth/AuthProvider';
import { Permissions } from '#auth/types';
import { borrarUso, descargarUso, setUsoActivado, usoActivado } from '#uso';

import { Setting } from './UI';

/**
 * Ajustes → «Datos de uso»: qué se guarda y qué no, interruptor del permiso
 * (el mismo que el aviso `PermisoUso`) y descargar / borrar lo propio. Quien
 * administra el servidor puede bajar además los de todas las personas.
 * Detalle en docs-vadym/uso/README.md.
 */
export function UsoSettings() {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const esAdmin = hasPermission(Permissions.ADMINISTRATOR);
  const [activado, setActivado] = useState(usoActivado);
  const [estado, setEstado] = useState<
    'descargando' | 'confirmar' | 'borrando' | 'borrado' | 'error' | null
  >(null);

  const descargar = async (todos = false) => {
    setEstado('descargando');
    try {
      const texto = await descargarUso({ todos });
      const fecha = new Date().toISOString().slice(0, 10);
      await window.Actual.saveFile(
        texto,
        `rumbo-uso-${todos ? 'todos-' : ''}${fecha}.jsonl`,
        todos
          ? t("Download everyone's usage data")
          : t('Download my usage data'),
      );
      setEstado(null);
    } catch {
      setEstado('error');
    }
  };

  const borrar = async () => {
    // Doble toque: el primero pide confirmación.
    if (estado !== 'confirmar') {
      setEstado('confirmar');
      return;
    }
    setEstado('borrando');
    setEstado((await borrarUso()) ? 'borrado' : 'error');
  };

  return (
    <Setting
      primaryAction={
        <View style={{ gap: 10, width: '100%' }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
            <Toggle
              id="rumbo-uso-activado"
              aria-label={t('Help improve Rumbo')}
              isOn={activado}
              onToggle={valor => {
                setUsoActivado(valor);
                setActivado(valor);
              }}
            />
            <Text>
              {activado
                ? t('On: you help improve Rumbo')
                : t('Off: nothing is being saved')}
            </Text>
          </View>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10 }}>
            <Button
              onPress={() => void descargar()}
              isDisabled={estado === 'descargando'}
            >
              <Trans>Download my usage data</Trans>
            </Button>
            <Button
              onPress={() => void borrar()}
              isDisabled={estado === 'borrando'}
            >
              {estado === 'confirmar'
                ? t('Tap again to delete everything')
                : t('Delete my usage data')}
            </Button>
          </View>
          {esAdmin && (
            <View style={{ gap: 6 }}>
              <Text style={{ fontSize: 12, color: theme.pageTextSubdued }}>
                <Trans>Only for whoever runs the server</Trans>
              </Text>
              <View style={{ flexDirection: 'row' }}>
                <Button
                  onPress={() => void descargar(true)}
                  isDisabled={estado === 'descargando'}
                >
                  <Trans>Download everyone's usage data</Trans>
                </Button>
              </View>
            </View>
          )}
          {estado === 'borrado' && (
            <Text style={{ fontSize: 12 }}>
              <Trans>Usage data deleted.</Trans>
            </Text>
          )}
          {estado === 'error' && (
            <Text style={{ fontSize: 12, color: theme.errorText }}>
              {t('Could not reach the server. Try again later.')}
            </Text>
          )}
        </View>
      }
    >
      <Text>
        <Trans>
          <strong>What is noted:</strong> which screens you open and for how
          long, which windows and options you use, the notices and "Undo" you
          tap, errors, and your screen size, theme and scale. It is kept on the
          Rumbo server for up to 12 months and only used to improve the app.
        </Trans>
      </Text>
      <Text>
        <Trans>
          <strong>Never noted:</strong> amounts or balances, payee, account or
          category names, notes, IBANs or anything you type. If you turn it off,
          nothing more is noted from this device.
        </Trans>
      </Text>
    </Setting>
  );
}
